import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryService } from '../../../services/categoryService';
import './AdminCategories.css';

/**
 * Компонент для отображения списка категорий с возможностью
 * редактирования, удаления и добавления новых категорий
 */
const AdminCategoriesList = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /**
   * Загрузка списка категорий
   */
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categoryService.getAllCategories();
      // Убираем проверку на data, так как сервис возвращает массив напрямую
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
      setError(error.message || 'Не удалось загрузить список категорий');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * Обработчик удаления категории
   */
  const handleDeleteCategory = async (id, name) => {
    const isConfirmed = window.confirm(
      `Вы уверены, что хотите удалить категорию "${name}"?\n\n` +
      `Это действие нельзя отменить.`
    );
    
    if (!isConfirmed) return;
    
    try {
      setDeletingId(id);
      await categoryService.deleteCategory(id);
      
      setCategories(prevCategories => prevCategories.filter(cat => cat.id !== id));
      alert('Категория успешно удалена');
      
    } catch (error) {
      console.error('Ошибка удаления:', error);
      alert(error.message || 'Не удалось удалить категорию');
    } finally {
      setDeletingId(null);
    }
  };

  /**
   * Получение CSS класса для бейджа типа категории
   */
  const getTypeBadgeClass = (type) => {
    switch (type?.toLowerCase()) {
      case 'location':
      case 'локации':
        return 'type-locations';
      case 'route':
      case 'маршруты':
        return 'type-routes';
      default:
        return 'type-default';
    }
  };

  /**
   * Получение отображаемого названия типа
   */
  const getTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'location':
      case 'локации':
        return 'Локация';
      case 'route':
      case 'маршруты':
        return 'Маршрут';
      default:
        return type || '—';
    }
  };

  // Состояние загрузки
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading__spinner"></div>
        <p>Загрузка категорий...</p>
      </div>
    );
  }

  // Состояние ошибки
  if (error) {
    return (
      <div className="admin-error">
        <div className="admin-error__icon">⚠️</div>
        <p className="admin-error__message">{error}</p>
        <button 
          onClick={fetchCategories} 
          className="admin-error__retry"
        >
          Повторить попытку
        </button>
      </div>
    );
  }

  // Пустое состояние
  if (categories.length === 0) {
    return (
      <div className="admin-categories-list__empty">
        <div className="admin-empty-state__icon">📂</div>
        <h3>Категории не найдены</h3>
        <p>Создайте первую категорию, чтобы начать работу</p>
        <button 
          className="admin-categories-list__add-btn"
          onClick={() => navigate('/admin/categories/add')}
        >
          + Создать первую категорию
        </button>
      </div>
    );
  }

  // Основной рендер - таблица с данными
  return (
    <div className="admin-categories-list">
      {/* Заголовок с кнопкой добавления */}
      <div className="admin-categories-list__header">
        <div className="admin-categories-list__title-section">
          <h2>Управление категориями</h2>
          <span className="admin-categories-list__count">
            Всего: {categories.length} {getDeclension(categories.length)}
          </span>
        </div>
        <button 
          className="admin-categories-list__add-btn"
          onClick={() => navigate('/admin/categories/add')}
        >
          <span className="btn-icon">+</span>
          Добавить категорию
        </button>
      </div>

      {/* Таблица категорий */}
      <div className="admin-categories-list__table-wrapper">
        <table className="admin-categories-list__table">
          <thead>
            <tr>
              <th className="col-id">ID</th>
              <th className="col-name">Название</th>
              <th className="col-description">Описание</th>
              <th className="col-type">Тип</th>
              <th className="col-actions">Действия</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <tr key={category.id}>
                <td className="col-id">{category.id}</td>
                <td className="col-name">
                  <strong>{category.name}</strong>
                </td>
                <td className="col-description">
                  {category.description || <span className="text-muted">—</span>}
                </td>
                <td className="col-type">
                  <span className={`type-badge ${getTypeBadgeClass(category.type)}`}>
                    {getTypeLabel(category.type)}
                  </span>
                </td>
                <td className="col-actions">
                  <button 
                    className="action-btn edit-btn"
                    onClick={() => navigate(`/admin/categories/edit/${category.id}`)}
                    title="Редактировать категорию"
                  >
                    ✏️ Редактировать
                  </button>
                  <button 
                    className="action-btn delete-btn"
                    onClick={() => handleDeleteCategory(category.id, category.name)}
                    disabled={deletingId === category.id}
                    title="Удалить категорию"
                  >
                    {deletingId === category.id ? '⏳' : '🗑️ Удалить'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Вспомогательная функция для склонения слова "категория"
 */
function getDeclension(count) {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'категорий';
  }
  
  if (lastDigit === 1) {
    return 'категория';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'категории';
  }
  
  return 'категорий';
}

export default AdminCategoriesList;